import React, { useEffect, useMemo, useRef, useState } from 'react';
import { WEB_MAX_CONTENT_WIDTH } from '../../theme/webLayoutWidths';
import {
  Platform,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';
import {
  TextInput as PaperTextInput,
  Button as PaperButton,
} from 'react-native-paper';
import { useNavigation, useRoute } from '@react-navigation/native';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { GTFonts, useTheme } from '../../theme';
import {
  borderWidth,
  formatMoney,
  fs,
  roundMoney,
  s,
  v,
} from '../../utils/libs';
import { KeyboardStickyView } from 'react-native-keyboard-controller';
import KeyboardAwareFormScroll from '../../components/Reusables/KeyboardAwareFormScroll';
import useBackofficeAxios from '../../hooks/useBackofficeAxios';
import useBackofficeOwnerAuth from '../../hooks/useBackofficeOwnerAuth';
import {
  addPurchaseOrderInformation,
  getPurchaseOrderInformation,
  getSuppliersInformation,
  parsePurchaseOrderResponse,
  parseSuppliersResponse,
  updatePurchaseOrderInformation,
} from '../../api/backofficeSuppliers';
import { listOwnerProducts } from '../../api/backofficeProducts';
import { useAppDispatch, useAppSelector } from '../../redux/helper';
import {
  setPurchaseOrderByIdData,
  setSuppliersListData,
  upsertPurchaseOrderBySupplierId,
} from '../../redux/backoffice/suppliersSlice';
import useLocation from '../../hooks/useLocation';
import {
  buildProductFromLine,
  getProductDisplayName,
  parseNumberLike,
  type ProductLike,
} from '../../utils/backofficeSuppliers';
import { composeProductVariantLabel } from '../../utils/productDisplayName';
import {
  formatNumericStringWithCommas,
  getCurrencyDisplayFormat,
  sanitizeMoneyInput,
  sanitizeNumericInput,
} from '../../utils/libs';
import { getApiMessage } from '../../utils/apiError';
import {
  canSelectPurchaseOrderDeliveryLocation,
  resolvePurchaseOrderLocationId,
  toPurchaseOrderLocationOptions,
} from '../../utils/purchaseOrderLocation';
import ProductUnitAffix from '../products/newproducts/components/ProductUnitAffix';
import SupplierProductPicker from '../../components/suppliers/SupplierProductPicker';
import SupplierPrerequisiteEmptyState from '../../components/suppliers/SupplierPrerequisiteEmptyState';
import DatePickerField from '../../components/Reusables/DatePickerField';
import FormSelectField from '../../components/Reusables/FormSelectField';
import { AppSnackbar } from '../../services/AppSnackbar';
import appAnalytics from '../../services/appAnalytics';
import { buildAppAnalyticsContext } from '../../services/appAnalyticsContext';
import type { PurchaseOrderStatusProp } from '../../services/appAnalyticsTypes';
import type { RootStackParamList } from '../../navigation/AppStack';
import moment from 'moment';

type RouteParams = {
  supplierId?: string;
  purchaseOrderId?: string;
};

type POLineForm = {
  _id?: string;
  product: any | null;
  variantId?: string;
  variantDisplayName?: string;
  variantAttributes?: Record<string, string>;
  productName: string;
  quantity: string;
  unitPrice: string;
  category: string;
  subCategory: string;
  barcode: string;
};

const createLine = (): POLineForm => ({
  product: null,
  variantId: '',
  variantDisplayName: '',
  variantAttributes: {},
  productName: '',
  quantity: '',
  unitPrice: '',
  category: '',
  subCategory: '',
  barcode: '',
});

const getDefaultExpectedDate = (): Date => {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  return d;
};

const getProductVariants = (product: any): any[] =>
  Array.isArray(product?.bp_product_variants)
    ? product.bp_product_variants
    : [];

const getVariantDisplayName = (variant: any): string => {
  if (!variant || typeof variant !== 'object') return '';
  const explicit = String(variant?.bp_variant_display_name || '').trim();
  if (explicit) return explicit;
  const attrs = variant?.bp_variant_attributes;
  if (attrs && typeof attrs === 'object') {
    const joined = Object.values(attrs)
      .map(v => String(v || '').trim())
      .filter(Boolean)
      .join(' / ');
    if (joined) return joined;
  }
  const sku = String(variant?.bp_variant_sku || '').trim();
  return sku;
};

const getVariantFromLine = (line: POLineForm): any | null => {
  if (!line?.product || !line?.variantId) return null;
  return (
    getProductVariants(line.product).find(
      variant => String(variant?._id || '') === String(line.variantId || ''),
    ) || null
  );
};

const getLineDisplayName = (line: POLineForm): string => {
  const variant = getVariantFromLine(line);
  if (variant) {
    const variantName = getVariantDisplayName(variant);
    const base = String(line?.product?.bp_product_name || '').trim();
    if (variantName) {
      return composeProductVariantLabel(base, variantName);
    }
  }
  return (
    (line.product ? getProductDisplayName(line.product) : '') ||
    line.productName.trim() ||
    ''
  );
};

const BackOfficePurchaseOrderUpsertScreen: React.FC = () => {
  const { theme } = useTheme();
  const styles = useMemo(() => createStyles(theme), [theme]);
  const route = useRoute();
  const { supplierId, purchaseOrderId } = (route.params || {}) as RouteParams;
  const isEdit = Boolean(purchaseOrderId);
  const navigation =
    useNavigation<NativeStackNavigationProp<RootStackParamList>>();
  const dispatch = useAppDispatch();
  const axiosBackoffice = useBackofficeAxios();
  const { owner } = useBackofficeOwnerAuth();
  const {
    locations,
    fetchLocations,
    isLoading: locationsLoading,
  } = useLocation();
  const suppliers = useAppSelector(
    state => state.backofficeSuppliers.list.items,
  );
  const cachedPurchaseOrder = useAppSelector(state =>
    purchaseOrderId
      ? state.backofficeSuppliers.purchaseOrderById[String(purchaseOrderId)]
          ?.data || null
      : null,
  );
  const employee = useAppSelector(
    s => s.auth.inApp.account.employeeInfo as any,
  );
  const ownerId = String(employee?.bp_owner_id || '');
  const currentLocation = useAppSelector(
    s => s.location.currentLocation as any,
  );
  const isAllLocations = useAppSelector(s => s.location.isAllLocations);
  const currency = useAppSelector(
    state =>
      state.location.currentLocation?.bp_location_settings
        ?.bp_location_currency || 'KES',
  );
  const currencyText = useMemo(
    () => getCurrencyDisplayFormat(currency),
    [currency],
  );

  const [selectedSupplierId, setSelectedSupplierId] = useState(
    String(supplierId || ''),
  );
  const [locationId, setLocationId] = useState('');
  const [expectedDate, setExpectedDate] = useState<Date | null>(() =>
    isEdit ? null : getDefaultExpectedDate(),
  );
  const [notes, setNotes] = useState('');
  const [lineItems, setLineItems] = useState<POLineForm[]>([createLine()]);
  const [allProducts, setAllProducts] = useState<any[]>([]);
  const [loadingProducts, setLoadingProducts] = useState(true);
  const [loadingSuppliers, setLoadingSuppliers] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loadingPO, setLoadingPO] = useState(false);
  const hydratedPOIdRef = useRef<string>('');
  const hydratingPOIdRef = useRef<string>('');

  const parseQty = (value: unknown) => parseNumberLike(value);

  useEffect(() => {
    if (!owner?.accessToken) return;
    if (suppliers.length > 0) return;
    setLoadingSuppliers(true);
    let mounted = true;

    getSuppliersInformation(axiosBackoffice as any, 1, 100, 'active', {
      accessToken: owner.accessToken,
    })
      .then(res => {
        setLoadingSuppliers(false);
        if (!mounted) return;
        const parsed = parseSuppliersResponse(res);
        if (parsed?.success && parsed.suppliers) {
          dispatch(
            setSuppliersListData({
              suppliers: parsed.suppliers,
              total: parsed.total ?? parsed.suppliers.length,
              page: parsed.page ?? 1,
              limit: parsed.limit ?? 100,
            }),
          );
        }
      })
      .catch((error: any) => {
        setLoadingSuppliers(false);
        if (!mounted) return;
        AppSnackbar.show(
          getApiMessage(error, 'Failed to load suppliers'),
          'error',
        );
      })
      .finally(() => {
        if (mounted) setLoadingSuppliers(false);
      });

    return () => {
      mounted = false;
    };
  }, [axiosBackoffice, dispatch, owner?.accessToken, suppliers.length]);

  useEffect(() => {
    if ((locations?.length ?? 0) > 0) return;
    fetchLocations();
  }, [locations?.length, fetchLocations]);

  useEffect(() => {
    if (isEdit) return;
    const currentId = String(currentLocation?._id || '');
    if (!currentId) return;
    if (!isAllLocations) {
      setLocationId(currentId);
      return;
    }
    setLocationId(prev => prev || currentId);
  }, [isEdit, isAllLocations, currentLocation?._id]);

  useEffect(() => {
    if (!owner?.accessToken || !ownerId) {
      setLoadingProducts(false);
      return;
    }
    setLoadingProducts(true);
    listOwnerProducts(axiosBackoffice as any, ownerId, 1, 500, {
      accessToken: owner.accessToken,
    })
      .then(res => {
        if (res?.status !== 200) return;
        const docs = res?.data?.data?.data || [];
        setAllProducts(Array.isArray(docs) ? docs : []);
      })
      .catch(() => {})
      .finally(() => setLoadingProducts(false));
  }, [axiosBackoffice, owner?.accessToken, ownerId]);

  useEffect(() => {
    if (!isEdit || !purchaseOrderId) return;
    const poId = String(purchaseOrderId);

    const hydrateFormFromPO = (po: any) => {
      const supplierValue = String(
        (typeof po.bp_purchase_order_supplier_id === 'object'
          ? po.bp_purchase_order_supplier_id?._id
          : po.bp_purchase_order_supplier_id) || '',
      );
      setSelectedSupplierId(supplierValue || String(supplierId || ''));
      const locId =
        po.bp_purchase_order_location_id?._id ||
        po.bp_purchase_order_location_id ||
        '';
      setLocationId(String(locId));
      setExpectedDate(
        po.bp_purchase_order_expected_delivery_date
          ? new Date(
              String(po.bp_purchase_order_expected_delivery_date).slice(0, 10),
            )
          : null,
      );
      setNotes(String(po.bp_purchase_order_notes || ''));

      const items = Array.isArray(po.bp_purchase_order_line_items)
        ? po.bp_purchase_order_line_items
        : Array.isArray(po.lineItems)
          ? po.lineItems
          : [];
      const mapped = items.map((li: any) => {
        const productId = String(
          li?.bp_line_item_product_id?._id || li?.bp_line_item_product_id || '',
        );
        const embeddedProduct =
          li?.bp_line_item_product_id &&
          typeof li.bp_line_item_product_id === 'object'
            ? li.bp_line_item_product_id
            : null;
        const product =
          embeddedProduct ||
          allProducts.find(p => String(p?._id || '') === productId) ||
          buildProductFromLine(li) ||
          null;
        const rawQty = li?.bp_line_item_quantity ?? li?.bp_line_item_mass ?? '';
        const rawUnitPrice = li?.bp_line_item_unit_price ?? '';
        const qtyStr =
          rawQty === '' || rawQty == null
            ? ''
            : formatNumericStringWithCommas(String(rawQty));
        const upStr =
          rawUnitPrice === '' || rawUnitPrice == null
            ? ''
            : formatNumericStringWithCommas(String(rawUnitPrice));
        const variantId = String(li?.bp_line_item_variant_id || '');
        const variantAttributes =
          li?.bp_line_item_variant_attributes &&
          typeof li.bp_line_item_variant_attributes === 'object'
            ? li.bp_line_item_variant_attributes
            : {};
        const variantDisplayName = String(
          li?.bp_line_item_variant_display_name || '',
        ).trim();
        return {
          _id: li?._id ? String(li._id) : undefined,
          product,
          variantId,
          variantDisplayName,
          variantAttributes,
          productName: String(
            li?.bp_line_item_product_name ||
              getProductDisplayName(
                product || li?.bp_line_item_product_id || {},
              ),
          ),
          quantity: qtyStr,
          unitPrice: upStr,
          category: String(li?.bp_line_item_product_category || ''),
          subCategory: String(li?.bp_line_item_product_sub_category || ''),
          barcode: String(li?.bp_line_item_product_barcode || ''),
        } as POLineForm;
      });
      setLineItems(mapped.length > 0 ? mapped : [createLine()]);
      hydratedPOIdRef.current = poId;
      setLoadingPO(false);
    };

    if (hydratedPOIdRef.current === poId) return;

    if (cachedPurchaseOrder) {
      hydrateFormFromPO(cachedPurchaseOrder);
      return;
    }

    if (!owner?.accessToken) return;
    if (hydratingPOIdRef.current === poId) return;

    hydratingPOIdRef.current = poId;
    setLoadingPO(true);
    getPurchaseOrderInformation(axiosBackoffice as any, poId, {
      accessToken: owner.accessToken,
    })
      .then(res => {
        const parsed = parsePurchaseOrderResponse(res);
        const po = parsed?.purchaseOrder;
        if (!po) return;

        const resolvedPOId = String(po?._id || poId);
        dispatch(setPurchaseOrderByIdData({ id: resolvedPOId, data: po }));
        const resolvedSupplierId = String(
          (typeof po.bp_purchase_order_supplier_id === 'object'
            ? po.bp_purchase_order_supplier_id?._id
            : po.bp_purchase_order_supplier_id) || '',
        );
        if (resolvedSupplierId) {
          dispatch(
            upsertPurchaseOrderBySupplierId({
              supplierId: resolvedSupplierId,
              order: po,
            }),
          );
        }
        hydrateFormFromPO(po);
      })
      .catch(() => {})
      .finally(() => {
        hydratingPOIdRef.current = '';
        setLoadingPO(false);
      });
  }, [
    allProducts,
    axiosBackoffice,
    cachedPurchaseOrder,
    dispatch,
    isEdit,
    owner?.accessToken,
    purchaseOrderId,
    supplierId,
  ]);

  const subtotal = useMemo(() => {
    return lineItems.reduce((sum, line) => {
      const qty = parseQty(line.quantity);
      const unitPrice = parseQty(line.unitPrice);
      if (!(qty > 0)) return sum;
      return (
        sum + roundMoney(qty * (Number.isFinite(unitPrice) ? unitPrice : 0))
      );
    }, 0);
  }, [lineItems]);

  const supplierOptions = useMemo(
    () =>
      suppliers.map((supplier: any) => ({
        label: String(supplier?.bp_supplier_name || 'Unnamed supplier'),
        value: String(supplier?._id),
      })),
    [suppliers],
  );

  const locationOptions = useMemo(
    () =>
      toPurchaseOrderLocationOptions(locations, [
        currentLocation?._id,
        locationId,
      ]),
    [currentLocation?._id, locationId, locations],
  );

  const canSelectDeliveryLocation =
    canSelectPurchaseOrderDeliveryLocation(isAllLocations);

  const canSubmit = useMemo(() => {
    const supplierOk = Boolean(selectedSupplierId);
    const itemsOk = lineItems.some(
      line =>
        (line.product || line.productName.trim()) &&
        (!line.product?.bp_product_has_variants || Boolean(line.variantId)) &&
        parseQty(line.quantity) > 0,
    );
    return supplierOk && itemsOk && !saving && !loadingPO;
  }, [lineItems, loadingPO, saving, selectedSupplierId]);

  const updateLine = (index: number, updates: Partial<POLineForm>) => {
    setLineItems(prev =>
      prev.map((line, idx) => (idx === index ? { ...line, ...updates } : line)),
    );
  };

  const addLine = () => setLineItems(prev => [...prev, createLine()]);
  const removeLine = (index: number) =>
    setLineItems(prev => prev.filter((_, idx) => idx !== index));

  const handleSubmit = async () => {
    if (!canSubmit || !owner?.accessToken) return;

    const payload = {
      bp_purchase_order_supplier_id: selectedSupplierId,
      bp_purchase_order_location_id:
        resolvePurchaseOrderLocationId({
          isEdit,
          isAllLocations,
          formLocationId: locationId,
          currentLocationId: String(currentLocation?._id || ''),
        }) || undefined,
      bp_purchase_order_expected_delivery_date: expectedDate
        ? moment(expectedDate).format('YYYY-MM-DD')
        : undefined,
      bp_purchase_order_notes: notes.trim() || undefined,
      bp_purchase_order_line_items: lineItems
        .filter(
          line =>
            (line.product || line.productName.trim()) &&
            (!line.product?.bp_product_has_variants ||
              Boolean(line.variantId)) &&
            parseQty(line.quantity) > 0,
        )
        .map(line => {
          const selectedVariant = getVariantFromLine(line);
          const variantAttributes =
            selectedVariant?.bp_variant_attributes &&
            typeof selectedVariant.bp_variant_attributes === 'object'
              ? selectedVariant.bp_variant_attributes
              : line.variantAttributes &&
                  typeof line.variantAttributes === 'object'
                ? line.variantAttributes
                : undefined;
          const productNameForPayload = getLineDisplayName(line);
          const productBarcode =
            selectedVariant?.bp_variant_barcode ||
            line.barcode.trim() ||
            undefined;
          return {
            ...(line._id && { _id: line._id }),
            bp_line_item_product_id: line.product?._id || undefined,
            bp_line_item_variant_id: line.variantId || undefined,
            bp_line_item_variant_display_name:
              getVariantDisplayName(selectedVariant) ||
              line.variantDisplayName ||
              undefined,
            bp_line_item_variant_attributes: variantAttributes,
            bp_line_item_product_name: productNameForPayload,
            bp_line_item_quantity: parseQty(line.quantity),
            bp_line_item_unit_price: line.unitPrice
              ? parseQty(line.unitPrice)
              : undefined,
            bp_line_item_product_price_type:
              line.product?.bp_product_price_type || undefined,
            bp_line_item_unit_of_mass:
              line.product?.bp_product_unit_of_mass || undefined,
            bp_line_item_product_category: line.category.trim() || undefined,
            bp_line_item_product_sub_category:
              line.subCategory.trim() || undefined,
            bp_line_item_product_barcode: productBarcode,
          };
        }),
    };

    const lineItemsPayload = payload.bp_purchase_order_line_items;
    const lineItemCount = Array.isArray(lineItemsPayload)
      ? lineItemsPayload.length
      : 0;

    setSaving(true);
    try {
      if (isEdit && purchaseOrderId) {
        const res = await updatePurchaseOrderInformation(
          axiosBackoffice as any,
          purchaseOrderId,
          payload,
          { accessToken: owner.accessToken },
        );
        const ok = res?.data?.success === true || res?.data?.status === 200;
        AppSnackbar.show(
          String(
            res?.data?.message ||
              (ok ? 'Purchase order updated' : 'Update failed'),
          ),
          ok ? 'success' : 'error',
        );
        if (!ok) return;
        const parsed = parsePurchaseOrderResponse(res);
        const updatedOrder = parsed?.purchaseOrder;
        if (updatedOrder) {
          const updatedPOId = String(updatedOrder?._id || purchaseOrderId);
          const normalizedOrder = { ...updatedOrder, _id: updatedPOId };
          dispatch(
            setPurchaseOrderByIdData({
              id: updatedPOId,
              data: normalizedOrder,
            }),
          );
          dispatch(
            upsertPurchaseOrderBySupplierId({
              supplierId: selectedSupplierId,
              order: normalizedOrder as any,
            }),
          );
        }
        const updatedStatus = String(
          updatedOrder?.bp_purchase_order_status || '',
        ) as PurchaseOrderStatusProp;
        appAnalytics.track('procurement_po_updated', {
          ...buildAppAnalyticsContext(),
          line_item_count: lineItemCount,
          total_amount: Number(subtotal || 0),
          ...(updatedStatus ? { status: updatedStatus } : {}),
        });
        navigation.replace('BackOfficePurchaseOrderDetails', {
          supplierId: selectedSupplierId,
          purchaseOrderId,
        });
        return;
      }

      const res = await addPurchaseOrderInformation(
        axiosBackoffice as any,
        payload,
        {
          accessToken: owner.accessToken,
        },
      );
      console.log('addPurchaseOrderInformationRes', res);
      const ok = res?.data?.success === true || res?.data?.status === 200;
      const parsed = parsePurchaseOrderResponse(res);
      const createdOrder = parsed?.purchaseOrder;
      const createdPOId = String(
        createdOrder?._id ||
          res?.data?.purchaseOrder?._id ||
          res?.data?.data?._id ||
          '',
      );
      AppSnackbar.show(
        String(
          res?.data?.message ||
            (ok ? 'Purchase order created' : 'Create failed'),
        ),
        ok ? 'success' : 'error',
      );
      if (!ok || !createdPOId) return;
      if (createdOrder) {
        const normalizedOrder = { ...createdOrder, _id: createdPOId };
        dispatch(
          setPurchaseOrderByIdData({
            id: createdPOId,
            data: normalizedOrder,
          }),
        );
        dispatch(
          upsertPurchaseOrderBySupplierId({
            supplierId: selectedSupplierId,
            order: normalizedOrder as any,
          }),
        );
      }
      const createdStatus = String(
        createdOrder?.bp_purchase_order_status || 'draft',
      ) as PurchaseOrderStatusProp;
      appAnalytics.track('procurement_po_created', {
        ...buildAppAnalyticsContext(),
        line_item_count: lineItemCount,
        total_amount: Number(subtotal || 0),
        status: createdStatus || 'draft',
      });
      navigation.replace('BackOfficePurchaseOrderDetails', {
        supplierId: selectedSupplierId,
        purchaseOrderId: createdPOId,
      });
    } catch (error: any) {
      AppSnackbar.show(
        getApiMessage(error, 'Failed to save purchase order'),
        'error',
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <View style={styles.container}>
      <KeyboardAwareFormScroll
        style={styles.scroll}
        contentContainerStyle={styles.content}
        keyboardShouldPersistTaps="handled"
        // baseBottomPadding={v(80)}
        bottomOffset={v(240)}>
        <Text style={styles.subtitle}>
          {isEdit
            ? 'Update the purchase-order details and items.'
            : 'Create a purchase order to order items from your supplier.'}
        </Text>

        <Text style={styles.sectionTitle}>Purchase Order Details</Text>

        {!loadingSuppliers &&
        supplierOptions.length === 0 &&
        !selectedSupplierId ? (
          <SupplierPrerequisiteEmptyState
            description="Add a supplier before creating a purchase order."
            onAddSupplier={() => navigation.navigate('BackOfficeAddSupplier')}
          />
        ) : (
          <FormSelectField
            label="Supplier *"
            value={selectedSupplierId}
            onValueChange={setSelectedSupplierId}
            options={supplierOptions}
            disabled={Boolean(supplierId) || loadingPO}
            placeholder="Select supplier"
            loading={loadingSuppliers}
          />
        )}

        <FormSelectField
          label="Delivery Location"
          value={locationId}
          onValueChange={setLocationId}
          options={locationOptions}
          disabled={loadingPO || !canSelectDeliveryLocation}
          placeholder="Select location"
          loading={locationsLoading && (locations?.length ?? 0) === 0}
        />

        <DatePickerField
          value={expectedDate}
          onChange={setExpectedDate}
          label="Expected Delivery Date"
          placeholder="Select date"
          optional
          disabled={loadingPO}
          displayFormat="iso"
          minimumDate={new Date()}
        />

        <View style={styles.linesHeader}>
          <Text style={styles.sectionTitle}>Items</Text>
        </View>

        {loadingPO ? (
          <Text style={styles.loadingText}>
            Loading purchase-order details...
          </Text>
        ) : (
          lineItems.map((line, index) => (
            <View
              key={`${line._id || 'line'}-${index}`}
              style={styles.lineCard}>
              <View style={styles.lineTop}>
                <Text style={styles.lineTitle}>Item {index + 1}</Text>
                {lineItems.length > 1 ? (
                  <TouchableOpacity
                    style={styles.inlineAction}
                    onPress={() => removeLine(index)}>
                    <Text
                      style={[styles.inlineActionLabel, styles.removeLabel]}>
                      Remove
                    </Text>
                  </TouchableOpacity>
                ) : null}
              </View>

              <SupplierProductPicker
                products={allProducts}
                selectedProduct={line.product}
                loadingProducts={loadingProducts}
                onAddProduct={() => navigation.navigate('BackOfficeAddProduct')}
                onSelectProduct={product => {
                  const variants = getProductVariants(product);
                  const defaultVariant =
                    product?.bp_product_has_variants && variants.length > 0
                      ? variants[0]
                      : null;
                  const defaultBuyingPrice =
                    defaultVariant?.bp_variant_buying_price != null
                      ? defaultVariant.bp_variant_buying_price
                      : product?.bp_product_buying_price;
                  const unitPriceVal =
                    defaultBuyingPrice != null
                      ? formatNumericStringWithCommas(
                          String(defaultBuyingPrice),
                        )
                      : '';
                  const defaultVariantDisplayName =
                    getVariantDisplayName(defaultVariant);
                  updateLine(index, {
                    product,
                    productName: defaultVariant
                      ? composeProductVariantLabel(
                          product?.bp_product_name,
                          defaultVariantDisplayName,
                        )
                      : getProductDisplayName(product as ProductLike),
                    variantId: defaultVariant?._id
                      ? String(defaultVariant._id)
                      : '',
                    variantDisplayName: defaultVariantDisplayName,
                    variantAttributes:
                      defaultVariant?.bp_variant_attributes || {},
                    category: String(product?.bp_product_category || ''),
                    subCategory: String(product?.bp_product_sub_category || ''),
                    barcode: String(
                      defaultVariant?.bp_variant_barcode ||
                        product?.bp_product_barcode ||
                        '',
                    ),
                    unitPrice: unitPriceVal,
                  });
                }}
              />

              {line.product?.bp_product_has_variants &&
              getProductVariants(line.product).length > 0 ? (
                <FormSelectField
                  label="Variant *"
                  value={String(line.variantId || '')}
                  onValueChange={value => {
                    const variantId = String(value || '');
                    const selectedVariant = getProductVariants(
                      line.product,
                    ).find(variant => String(variant?._id || '') === variantId);
                    const selectedVariantName =
                      getVariantDisplayName(selectedVariant);
                    const variantBuyingPrice =
                      selectedVariant?.bp_variant_buying_price;
                    const parentName = String(
                      line.product?.bp_product_name || '',
                    ).trim();
                    updateLine(index, {
                      variantId,
                      variantDisplayName: selectedVariantName,
                      variantAttributes:
                        selectedVariant?.bp_variant_attributes || {},
                      barcode: String(
                        selectedVariant?.bp_variant_barcode || '',
                      ),
                      unitPrice:
                        variantBuyingPrice != null
                          ? formatNumericStringWithCommas(
                              String(variantBuyingPrice),
                            )
                          : line.unitPrice,
                      productName: composeProductVariantLabel(
                        parentName,
                        selectedVariantName,
                      ),
                    });
                  }}
                  options={getProductVariants(line.product).map(
                    (variant: any) => ({
                      label: getVariantDisplayName(variant) || 'Variant',
                      value: String(variant?._id || ''),
                    }),
                  )}
                  placeholder="Select variant"
                />
              ) : null}

              <View style={styles.lineItemRow}>
                <View style={styles.lineItemField}>
                  <Text style={styles.lineItemLabel}>Qty *</Text>
                  <PaperTextInput
                    mode="outlined"
                    value={line.quantity}
                    keyboardType="decimal-pad"
                    onChangeText={text =>
                      updateLine(index, {
                        quantity: formatNumericStringWithCommas(
                          sanitizeNumericInput(text, {
                            isUnit:
                              line.product?.bp_product_price_type === 'unit',
                          }),
                        ),
                      })
                    }
                    placeholder="0"
                    style={styles.lineInput}
                    right={ProductUnitAffix({
                      priceType: String(
                        line.product?.bp_product_price_type || '',
                      ),
                      unitOfMass: String(
                        line.product?.bp_product_unit_of_mass || '',
                      ),
                      kind: 'stock',
                    })}
                  />
                </View>
                <View style={styles.lineItemField}>
                  <Text style={styles.lineItemLabel}>Unit Price</Text>
                  <PaperTextInput
                    mode="outlined"
                    value={line.unitPrice}
                    keyboardType="decimal-pad"
                    onChangeText={text =>
                      updateLine(index, {
                        unitPrice: formatNumericStringWithCommas(
                          sanitizeMoneyInput(text),
                        ),
                      })
                    }
                    placeholder="0"
                    style={styles.lineInput}
                    right={ProductUnitAffix({
                      priceType: String(
                        line.product?.bp_product_price_type || '',
                      ),
                      unitOfMass: String(
                        line.product?.bp_product_unit_of_mass || '',
                      ),
                      kind: 'price',
                    })}
                  />
                </View>
              </View>

              <View style={styles.lineTotalRow}>
                <Text style={styles.lineTotalLabel}>Total:</Text>
                <Text style={styles.lineTotalValue}>
                  {currencyText}{' '}
                  {formatMoney(
                    (parseQty(line.quantity) || 0) *
                      (parseQty(line.unitPrice) || 0),
                  )}
                </Text>
              </View>
            </View>
          ))
        )}

        {!loadingPO ? (
          <View style={styles.addLineRow}>
            <TouchableOpacity
              onPress={addLine}
              style={[styles.inlineAction, styles.addLineAction]}>
              <Text style={styles.inlineActionLabel}>+ Add Item</Text>
            </TouchableOpacity>
          </View>
        ) : null}

        <View style={styles.summaryCard}>
          <Text style={styles.summaryLabel}>
            Subtotal: {currencyText} {formatMoney(subtotal)}
          </Text>
        </View>

        <Text style={styles.sectionTitle}>Notes</Text>

        <PaperTextInput
          mode="outlined"
          label="Notes (optional)"
          value={notes}
          onChangeText={setNotes}
          editable={!loadingPO}
          multiline
          placeholder="Add a note for this purchase order"
          style={[styles.input, { height: v(80) }]}
        />
      </KeyboardAwareFormScroll>

      <KeyboardStickyView offset={{ closed: 0, opened: v(30) }}>
        <View style={styles.stickyButtonContainer}>
          <View style={styles.actions}>
            <PaperButton
              mode="outlined"
              onPress={() => navigation.goBack()}
              disabled={saving}
              style={styles.actionButton}
              contentStyle={[styles.actionButtonContent, { height: v(50) }]}
              labelStyle={styles.actionLabel}
              textColor={theme.colors.textSecondary}
              theme={{ colors: { outline: theme.colors.border } }}
              uppercase={false}>
              Cancel
            </PaperButton>
            <PaperButton
              mode="contained"
              onPress={handleSubmit}
              disabled={!canSubmit}
              loading={saving}
              style={styles.actionButton}
              labelStyle={styles.actionLabel}
              contentStyle={[styles.actionButtonContent, { height: v(50) }]}
              buttonColor={theme.colors.successDark}
              textColor={theme.colors.textInverse}
              uppercase={false}>
              {saving
                ? 'Saving...'
                : isEdit
                  ? 'Update Purchase order'
                  : 'Create Purchase order'}
            </PaperButton>
          </View>
        </View>
      </KeyboardStickyView>
    </View>
  );
};

const createStyles = (theme: any) =>
  StyleSheet.create({
    container: {
      flex: 1,
      backgroundColor: theme.colors.background,
    },
    scroll: {
      flex: 1,
      backgroundColor: theme.colors.surface,
      ...(Platform.OS === 'web' && {
        maxWidth: WEB_MAX_CONTENT_WIDTH,
        alignSelf: 'center',
        width: '100%',
      }),
    },
    content: {
      padding: s(12),
      gap: v(10),
      paddingBottom: v(24),
      ...(Platform.OS === 'web' && {
        padding: s(24),
        gap: v(20),
      }),
    },
    subtitle: {
      fontFamily: GTFonts.regular,
      fontSize: fs(13),
      lineHeight: fs(18),
      color: theme.colors.textSecondary,
    },
    input: {
      backgroundColor: theme.colors.surface,
      height: v(50),
      marginBottom: v(6),
      borderRadius: theme.borderRadius.md,
      ...(Platform.OS === 'web' && {
        height: v(56),
      }),
    },
    linesHeader: {
      marginTop: v(4),
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
    },
    sectionTitle: {
      fontFamily: GTFonts.semibold,
      fontSize: fs(16),
      lineHeight: fs(18),
      color: theme.colors.textDark,
      marginVertical: v(6),
      ...(Platform.OS === 'web' && {
        fontSize: fs(18),
        marginBottom: v(12),
        paddingBottom: v(8),
        borderBottomWidth: 1,
        borderBottomColor: theme.colors.borderGray,
      }),
    },
    inlineAction: {
      paddingHorizontal: s(8),
      paddingVertical: v(4),
    },
    inlineActionLabel: {
      fontFamily: GTFonts.semibold,
      fontSize: fs(12),
      lineHeight: fs(16),
      color: theme.colors.primary,
    },
    removeLabel: {
      color: theme.colors.errorDark,
    },
    addLineRow: {
      flexDirection: 'row',
      justifyContent: 'flex-start',
      marginTop: v(2),
    },
    addLineAction: {
      paddingHorizontal: s(10),
      paddingVertical: v(6),
    },
    loadingText: {
      fontFamily: GTFonts.regular,
      fontSize: fs(13),
      lineHeight: fs(17),
      color: theme.colors.textSecondary,
    },
    lineCard: {
      borderWidth: 1,
      borderColor: theme.colors.border,
      borderRadius: theme.borderRadius.md,
      padding: s(10),
      gap: v(8),
      backgroundColor: theme.colors.surface,
      shadowOpacity: 0,
      shadowRadius: 0,
      elevation: 0,
    },
    lineTop: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
    },
    lineTitle: {
      fontFamily: GTFonts.semibold,
      fontSize: fs(13),
      lineHeight: fs(17),
      color: theme.colors.text,
    },
    lineItemRow: {
      flexDirection: 'row',
      gap: s(8),
    },
    lineItemField: {
      flex: 1,
      gap: v(4),
    },
    lineItemLabel: {
      fontFamily: GTFonts.semibold,
      fontSize: fs(14),
      lineHeight: fs(16),
      color: theme.colors.textSecondary,
    },
    lineInput: {
      backgroundColor: theme.colors.surface,
      height: v(50),
      marginBottom: v(6),
      borderRadius: theme.borderRadius.md,
      ...(Platform.OS === 'web' && {
        height: v(56),
      }),
    },
    lineTotalRow: {
      flexDirection: 'row',
      justifyContent: 'flex-end',
      alignItems: 'center',
      paddingTop: v(8),
      borderTopWidth: StyleSheet.hairlineWidth,
      borderTopColor: theme.colors.border,
    },
    lineTotalLabel: {
      fontFamily: GTFonts.semibold,
      fontSize: fs(13),
      lineHeight: fs(17),
      color: theme.colors.textSecondary,
      marginRight: s(6),
    },
    lineTotalValue: {
      fontFamily: GTFonts.semibold,
      fontSize: fs(14),
      lineHeight: fs(18),
      color: theme.colors.text,
    },
    summaryCard: {
      marginTop: v(4),
      borderRadius: theme.borderRadius.md,
      backgroundColor: theme.colors.surface,
      borderWidth: 1,
      borderColor: theme.colors.border,
      padding: s(10),
    },
    summaryLabel: {
      fontFamily: GTFonts.semibold,
      fontSize: fs(14),
      lineHeight: fs(18),
      color: theme.colors.text,
    },
    stickyButtonContainer: {
      backgroundColor: theme.colors.surface,
      paddingHorizontal: s(8),
      paddingVertical: s(12),
      borderTopWidth: borderWidth(Platform.OS === 'web' ? 0.5 : 1),
      borderTopColor: theme.colors.border,
      ...(Platform.OS === 'web' && {
        maxWidth: WEB_MAX_CONTENT_WIDTH,
        alignSelf: 'center',
        width: '100%',
        paddingHorizontal: s(24),
        borderTopWidth: 0,
      }),
    },
    actions: {
      flexDirection: 'row',
      gap: s(8),
    },
    actionButton: {
      flex: 1,
      borderRadius: theme.borderRadius.md,
    },
    actionButtonContent: {
      justifyContent: 'center',
      alignItems: 'center',
    },
    actionLabel: {
      fontFamily: GTFonts.semibold,
      fontSize: fs(14),
      lineHeight: fs(18),
      textAlign: 'center',
    },
  });

export default BackOfficePurchaseOrderUpsertScreen;
